/**
 * Node Search Modal Component
 * Search and add new nodes to workflow
 *
 * Integrates port-based selection rules to filter available nodes
 * based on the port type that was clicked
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { SearchIcon, NodeCategoryIcon, renderFiIcon } from '@bizfirst/common-react';
import { Input } from '../Shared/FormUI';
import { NodeType, getNodeDisplayProps } from '@flow-studio/types';
import { ModalPortal } from './ModalPortal';
import {
  getPortSelectionRule,
  validateNodeForPort,
  PortType,
  NodeCategory,
} from '../../rules/portNodeSelectionRules';

// Scrollbar styling for webkit browsers + responsive grid
const scrollbarStyles = `
  .node-search-content::-webkit-scrollbar {
    width: 8px;
  }
  .node-search-content::-webkit-scrollbar-track {
    background: transparent;
  }
  .node-search-content::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  .node-search-content::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
  .node-search-content.dark::-webkit-scrollbar-thumb {
    background: #4b5563;
  }
  .node-search-content.dark::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  .node-search-content {
    display: flex !important;
    flex-direction: column;
    width: 100% !important;
    height: 100% !important;
    overflow-y: auto !important;
  }

  .node-search-content.dark {
    display: flex !important;
    flex-direction: column;
    width: 100% !important;
    height: 100% !important;
    overflow-y: auto !important;
  }

  .node-search-grid {
    display: grid !important;
    gap: 12px;
    width: 100% !important;
  }

  /* Desktop: 4 columns */
  @media (min-width: 1400px) {
    .node-search-grid {
      grid-template-columns: repeat(4, 1fr) !important;
    }
  }

  /* Tablet to Large: 3 columns */
  @media (min-width: 1024px) and (max-width: 1399px) {
    .node-search-grid {
      grid-template-columns: repeat(3, 1fr) !important;
    }
  }

  /* Tablet: 2 columns */
  @media (max-width: 1023px) {
    .node-search-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }
`;

interface NodeSearchModalProps {
  sourceNodeId?: string;
  sourceHandleId?: string;
  position?: { x: number; y: number };
  onClose: () => void;
}

export const NodeSearchModal: React.FC<NodeSearchModalProps> = ({
  sourceNodeId,
  sourceHandleId,
  position,
  onClose,
}) => {
  const { nodeTypes, nodeCategories, addNode, addEdge, nodes, edges } = useWorkflowStore();
  const { theme, showToast } = useUIStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [hoveredNodeCode, setHoveredNodeCode] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-scroll to top when search term or category changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [searchTerm, selectedCategory]);

  // Debug: Log when modal opens and check nodeTypes availability
  useEffect(() => {
    // Check for duplicate node codes in the data
    const codeCounts: Record<string, number> = {};
    const duplicates: string[] = [];
    nodeTypes.forEach(nt => {
      codeCounts[nt.code] = (codeCounts[nt.code] || 0) + 1;
      if (codeCounts[nt.code] > 1 && !duplicates.includes(nt.code)) {
        duplicates.push(nt.code);
      }
    });

    console.log('[NodeSearchModal] Modal opened with state:', {
      nodeTypesLoaded: nodeTypes.length > 0,
      nodeTypesCount: nodeTypes.length,
      nodeTypesAvailable: nodeTypes.length > 0 ? '✅' : '❌',
      categoriesCount: nodeCategories.length,
      sourceNodeId,
      sourceHandleId,
      ...(duplicates.length > 0 && {
        duplicateNodeCodes: duplicates,
        note: 'Duplicates are handled by index-based keys, no nodes are filtered',
      }),
    });
    if (nodeTypes.length === 0) {
      console.warn('[NodeSearchModal] ⚠️ WARNING: nodeTypes is empty! Search will show no results.');
      console.warn('[NodeSearchModal] Make sure initializeNodeTemplates() completed successfully in WorkflowApp.tsx');
    }
  }, [nodeTypes]);

  // Get port type from handle ID (map handle IDs to port types)
  // Handle IDs come from node components (CustomHandle id attributes)
  const getPortTypeFromHandle = (handleId?: string): PortType => {
    if (!handleId) return 'output';
    switch (handleId.toLowerCase()) {
      case 'trigger':
        return 'trigger';
      case 'llm':
        return 'llm';
      case 'memory':
        return 'memory';
      case 'tool':
        // Tool is an output port for tools
        return 'tool';
      case 'extends':
        return 'agentInherit';
      case 'team-leader':
        return 'agentParticipation';
      case 'base-node':
        return 'agentInheritTarget';
      case 'team-member':
        return 'agentParticipantTarget';
      case 'knowledgebase':
      case 'knowledgeBase':
        return 'knowledgeBase';
      case 'my-components':
        return 'component';
      case 'input':
      case 'main':
        return 'input';
      default:
        // Default to output for unknown handles
        console.log('[NodeSearchModal] Unknown handle ID:', handleId, 'defaulting to output');
        return 'output';
    }
  };

  // Get source node to check its category
  const sourceNode = sourceNodeId ? nodes.find(n => n.id === sourceNodeId) : null;
  const sourceNodeCategory = sourceNode?.data?.processElement
    ? getNodeDisplayProps(sourceNode.data).category
    : undefined;

  // Get base port type from handle ID
  const basePortType = getPortTypeFromHandle(sourceHandleId);

  // Calculate final port type with special cases
  const determinedPortType: PortType = (() => {
    // Special case: If output port is from a tool node, only allow tool nodes
    if (basePortType === 'output' && sourceNodeCategory === 'tool') {
      console.log('[NodeSearchModal] 🔧 TOOL CHAINING DETECTED - Restricting to tool nodes only');
      return 'tool';
    }
    return basePortType;
  })();

  const portRule = getPortSelectionRule(determinedPortType);

  // Debug: Log source node detection
  useEffect(() => {
    console.log('[NodeSearchModal] Source Node Detection:', {
      sourceNodeId,
      sourceNodeFound: !!sourceNode,
      sourceNodeCategory,
      sourceNodeDisplayName: sourceNode?.data?.processElement ? getNodeDisplayProps(sourceNode.data).displayName : undefined,
      basePortType,
      determinedPortType,
      isToolOutput: basePortType === 'output' && sourceNodeCategory === 'tool',
    });
  }, [sourceNodeId, sourceNode, sourceNodeCategory, basePortType, determinedPortType]);

  // Debug logging
  useEffect(() => {
    console.log('[NodeSearchModal] Final Port Rule Applied:', {
      sourceHandleId,
      sourceNodeCategory,
      basePortType,
      determinedPortType,
      allowedCategories: portRule.allowedNodeCategories,
      description: portRule.description,
    });
  }, [sourceHandleId, sourceNodeCategory, determinedPortType, portRule]);

  // Filter categories based on port rules, reusing store categories
  const allowedCategories = portRule.allowedNodeCategories;
  const categories = (nodeCategories || [])
    .filter(cat => allowedCategories.includes(cat.code as NodeCategory))
    .map(cat => cat.code);

  // If a category was selected and is no longer allowed, reset it
  useEffect(() => {
    if (selectedCategory && !allowedCategories.includes(selectedCategory as NodeCategory)) {
      console.log('[NodeSearchModal] Resetting selected category because it\'s no longer allowed');
      setSelectedCategory('');
    }
  }, [sourceHandleId, determinedPortType, selectedCategory, allowedCategories]);

  const filteredNodes = useMemo(() => {
    return nodeTypes.filter((nodeType) => {
      // First check if node category is allowed by port rules
      const categoryAllowed = allowedCategories.includes(nodeType.category as NodeCategory);

      // Then check search filter (displayName and description only, matching LeftToolbar logic)
      const matchesSearch =
        (nodeType.displayName?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (nodeType.description?.toLowerCase() ?? '').includes(searchTerm.toLowerCase());

      // Check category filter
      const matchesCategory = !selectedCategory || nodeType.category === selectedCategory;

      // Finally, validate the connection
      const validation = validateNodeForPort(
        nodeType.category as NodeCategory,
        determinedPortType
      );

      const isIncluded = categoryAllowed && matchesSearch && matchesCategory && validation.isValid;

      return isIncluded;
    });
  }, [nodeTypes, allowedCategories, searchTerm, selectedCategory, determinedPortType]);


  const handleNodeMouseEnter = useCallback((code: string) => {
    setHoveredNodeCode(code);
  }, []);

  const handleNodeMouseLeave = useCallback(() => {
    setHoveredNodeCode(null);
  }, []);

  const handleSelectNode = useCallback((nodeType: NodeType) => {
    // Validate node can be connected to this port
    const validation = validateNodeForPort(
      nodeType.category as NodeCategory,
      determinedPortType
    );

    if (!validation.isValid) {
      console.error('[NodeSearchModal] Invalid connection:', validation.reason);
      showToast('warning', `Cannot connect: ${validation.reason}`);
      return;
    }

    // Calculate position for new node
    let newPosition = position
      ? { x: position.x + 250, y: position.y - 50 }
      : { x: 400, y: 200 };

    // If we have a source node, position relative to it
    if (sourceNodeId) {
      const sourceNode = nodes.find(n => n.id === sourceNodeId);
      if (sourceNode) {
        // Position to the right and slightly below for right-side handles
        // Position below for bottom handles (like AI Agent's LLM/Memory/Tool)
        if (sourceHandleId === 'tool' || sourceHandleId === 'base-node' || sourceHandleId === 'team-leader') {
          // Bottom handles - position below
          newPosition = {
            x: sourceNode.position.x - 40,
            y: sourceNode.position.y + 300
          };
        } else if (sourceHandleId === 'llm' || sourceHandleId === 'memory') {
          // Top handles - position above
          newPosition = {
            x: sourceNode.position.x - 40,
            y: sourceNode.position.y - 300
          };
        } else if (sourceHandleId === 'extends' || sourceHandleId === 'team-member') {
          // Left handles (Extends, Sub Agent) - position to the left
          newPosition = {
            x: sourceNode.position.x - 400,
            y: sourceNode.position.y
          };
        } else if (sourceHandleId === 'my-components') {
          // my-components is on the left side — place component node to the left
          const existingComponentCount = edges.filter(
            e => e.source === sourceNodeId && e.sourceHandle === 'my-components'
          ).length;
          newPosition = {
            x: sourceNode.position.x - 360,
            y: sourceNode.position.y + existingComponentCount * 200,
          };
        } else {
          // Right-side handles - position to the right
          newPosition = {
            x: sourceNode.position.x + 400,
            y: sourceNode.position.y
          };
        }
      }
    }

    // Create new node with the correct WorkflowNodeData structure
    const newNodeId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const isTrigger = nodeType.code.includes('trigger');

    const newNode = {
      id: newNodeId,
      type: nodeType.code,
      position: newPosition,
      state: 'new' as const,
      data: {
        processElement: {
          name: `${nodeType.code}-${Date.now()}`,
          displayName: nodeType.displayName,
          description: nodeType.description,
          isTrigger,
          isDisabled: false,
          continueOnFail: false,
          alwaysOutputData: false,
          retryOnFail: false,
          extensionID: nodeType.extensionId ?? 1,
          configuration: {
            designer: {
              ui: {
                nodeType: nodeType.code,
                color: nodeType.color,
                iconUrl: nodeType.iconUrl,
                icon: nodeType.icon,
                iconStyle: nodeType.iconStyle,
                category: nodeType.category,
              },
            },
          },
          ports: [
            ...(nodeType.defaultInputPorts ?? []).map((p, i) => ({
              ...p,
              portType: 'input' as const,
              portIndex: i,
            })),
            ...(nodeType.defaultOutputPorts ?? []).map((p, i) => ({
              ...p,
              portType: 'output' as const,
              portIndex: i,
            })),
          ],
          connector: { configuration: {} },
        },
        runtime: {
          executionStatus: 'idle' as const,
          collapsed: false,
        },
      },
    };

    addNode(newNode as any);

    // If source node specified, create connection
    if (sourceNodeId && sourceHandleId) {
      // Map source handle IDs to their semantically correct target port on the connected node.
      // E.g. clicking "Sub Agents" (team-leader) should wire to "team-member" (Sub Agent)
      // on the target agent, not its generic "main" input.
      const sourceToTargetHandleMap: Record<string, string> = {
        'team-leader':   'team-member',  // Sub Agents → Sub Agent
        'extends':       'base-node',    // Extends    → Base Agent
        'my-components': 'component',    // Component  → Component (system port, always present)
      };

      let targetHandle = 'main'; // Default fallback

      // my-components always wires to the system 'component' port — no lookup needed
      if (sourceHandleId === 'my-components') {
        targetHandle = 'component';
      } else if (nodeType.defaultInputPorts && nodeType.defaultInputPorts.length > 0) {
        // Try to get from default input ports
        const preferredPortKey = sourceToTargetHandleMap[sourceHandleId];
        const preferredPort = preferredPortKey
          ? nodeType.defaultInputPorts.find(p => p.portKey === preferredPortKey)
          : undefined;
        targetHandle = preferredPort
          ? preferredPort.portKey
          : nodeType.defaultInputPorts[0].portKey;
      }

      // Target/input handles: new node connects INTO the AI Agent (reversed direction)
      const INPUT_TARGET_HANDLES = ['tool', 'llm', 'memory', 'base-node', 'team-member'];
      if (INPUT_TARGET_HANDLES.includes(sourceHandleId)) {
        const outputPortKey = nodeType.defaultOutputPorts?.[0]?.portKey ?? 'main';
        console.log('[NodeSearchModal] Creating reversed edge (input handle):', {
          source: newNodeId,
          target: sourceNodeId,
          sourceHandle: outputPortKey,
          targetHandle: sourceHandleId,
          targetNodeType: nodeType.code,
        });
        addEdge({
          id: `edge-${Date.now()}`,
          source: newNodeId,
          target: sourceNodeId,
          sourceHandle: outputPortKey,
          targetHandle: sourceHandleId,
          type: 'smoothstep',
          animated: false,
        });
      } else {
        console.log('[NodeSearchModal] Creating edge:', {
          source: sourceNodeId,
          target: newNodeId,
          sourceHandle: sourceHandleId,
          targetHandle: targetHandle,
          targetNodeType: nodeType.code,
        });
        addEdge({
          id: `edge-${Date.now()}`,
          source: sourceNodeId,
          target: newNodeId,
          sourceHandle: sourceHandleId,
          targetHandle: targetHandle,
          type: 'smoothstep',
          animated: false,
        });
      }
    }

    onClose();
  }, [sourceNodeId, sourceHandleId, position, nodes, edges, addNode, addEdge, onClose, determinedPortType, showToast]);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    background: theme === 'dark' ? '#1f2937' : '#fff',
    borderRadius: '12px',
    maxWidth: '1600px',
    width: '95vw',
    height: '85vh',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme === 'dark'
      ? '0 20px 60px rgba(0, 0, 0, 0.8)'
      : '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
    overflow: 'hidden',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
    background: theme === 'dark' ? '#111827' : '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const titleGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 700,
    color: theme === 'dark' ? '#f3f4f6' : '#111827',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const portDescStyle: React.CSSProperties = {
    fontSize: '10px',
    color: '#94a3b8',
    marginTop: '2px',
    maxWidth: '100%',
    whiteSpace: 'normal',
    lineHeight: '1.4',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: theme === 'dark' ? '#9ca3af' : '#6b7280',
    padding: '0',
    width: '26px',
    height: '26px',
    borderRadius: '6px',
    transition: 'background 0.2s',
    flexShrink: 0,
    lineHeight: 1,
  };

  const categoriesStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '8px 12px',
    background: theme === 'dark' ? '#111827' : '#f9fafb',
    maxHeight: '80px',
    borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
  };

  const categoryButtonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '3px 10px',
    borderRadius: '0px',
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    margin: 0,
    background: isActive ? '#309B48' : (theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
    color: isActive ? '#fff' : (theme === 'dark' ? '#9ca3af' : '#6b7280'),
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
    lineHeight: '1.5',
  });

  const categoryButtonHover = (e: React.MouseEvent<HTMLButtonElement>, isActive: boolean, entering: boolean) => {
    if (isActive) return;
    (e.currentTarget as HTMLElement).style.background = entering
      ? (theme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')
      : (theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)');
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '16px 24px',
    scrollbarWidth: 'thin',
    scrollbarColor: theme === 'dark' ? '#4b5563 #111827' : '#d1d5db #f9fafb',
    minHeight: '0',  // Critical for flex children with overflow
  };

  const nodeListStyle: React.CSSProperties = {
    display: 'grid',
    gap: '12px',
    gridAutoFlow: 'dense',  // Allow flexible layout
    width: '100%',
  };

  const nodeCardStyle: React.CSSProperties = {
    padding: '16px',
    background: theme === 'dark' ? '#111827' : '#f9fafb',
    border: `1px solid #ff9900`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const nodeHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  };

  const nodeIconStyle: React.CSSProperties = {
    fontSize: '24px',
  };

  const renderNodeIcon = (nt: NodeType) => {
    const url = nt.iconUrl ?? '';
    const color = nt.color ?? '#6b7280';
    const bgStyle: React.CSSProperties = {
      width: 28,
      height: 28,
      borderRadius: '6px',
      background: color + '22',  // 13% opacity tint
      border: `1px solid ${color}44`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    };

    // Feather icon name (e.g. "FiGlobe") takes priority
    if (nt.icon) {
      const rendered = renderFiIcon(nt.icon, 14, color);
      if (rendered) return <span style={bgStyle}>{rendered}</span>;
    }
    // Only render as emoji if first codepoint is ≥ U+2000 (symbols/emoji range).
    // This rejects ASCII placeholders (?/??/???) and mojibake Latin-1 sequences (ðŸ¤–).
    const firstCp = url.codePointAt(0) ?? 0;
    if (url && !url.startsWith('http') && !url.startsWith('/') && firstCp >= 0x2000) {
      return <span style={bgStyle}><span style={{ fontSize: '14px', lineHeight: 1 }}>{url}</span></span>;
    }
    if (url && (url.startsWith('http') || url.startsWith('/'))) {
      // Remote / local image URL
      return <span style={bgStyle}><img src={url} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} /></span>;
    }
    // Fallback: category icon tinted with node color
    return (
      <span style={bgStyle}>
        <NodeCategoryIcon category={nt.category} size={14} color={color} />
      </span>
    );
  };

  const nodeNameStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: theme === 'dark' ? '#f3f4f6' : '#111827',
  };

  const nodeDescStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme === 'dark' ? '#9ca3af' : '#6b7280',
    lineHeight: '1.4',
  };

  const nodeCategoryStyle: React.CSSProperties = {
    fontSize: '10px',
    color: theme === 'dark' ? '#6b7280' : '#9ca3af',
    textTransform: 'uppercase',
    marginTop: '8px',
    letterSpacing: '0.5px',
  };

  return (
    <ModalPortal>
      <style>{scrollbarStyles}</style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header — title (+ description below it) + search inline */}
        <div style={headerStyle}>
          <div style={titleGroupStyle}>
            <span style={titleStyle}><SearchIcon size={14} /> Add Node</span>
            {sourceHandleId && portRule.description && (
              <span style={portDescStyle}>{portRule.description}</span>
            )}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ maxWidth: '320px', width: '100%' }}>
            <Input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search nodes..."
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onClose();
                }
              }}
            />
          </div>
          <button
            style={closeButtonStyle}
            onClick={onClose}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? '#3d3d3d' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        {/* Categories */}
        <div className="node-search-category-panel" style={categoriesStyle}>
          <button
            style={categoryButtonStyle(!selectedCategory)}
            onClick={() => setSelectedCategory('')}
            onMouseEnter={(e) => categoryButtonHover(e, !selectedCategory, true)}
            onMouseLeave={(e) => categoryButtonHover(e, !selectedCategory, false)}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              style={categoryButtonStyle(selectedCategory === category)}
              onClick={() => setSelectedCategory(category)}
              onMouseEnter={(e) => categoryButtonHover(e, selectedCategory === category, true)}
              onMouseLeave={(e) => categoryButtonHover(e, selectedCategory === category, false)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          style={contentStyle}
          className={`node-search-content ${theme === 'dark' ? 'dark' : ''}`}
        >
          <div
            key={`grid-${searchTerm}-${selectedCategory}-${determinedPortType}-${filteredNodes.length}`}
            className="node-search-grid"
            style={nodeListStyle}
          >
            {filteredNodes.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}><SearchIcon size={48} /></div>
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', textAlign: 'center' }}>
                  No nodes found
                </div>
                {nodeTypes.length === 0 ? (
                  <>
                    <div style={{ fontSize: '14px', marginBottom: '12px', textAlign: 'center', color: '#ef4444', fontWeight: 500 }}>
                      ⚠️ Node templates are not loaded
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px', textAlign: 'center', maxWidth: '400px' }}>
                      The node template service failed to initialize. Please refresh the page or contact support if the problem persists.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '14px', marginBottom: '12px', textAlign: 'center' }}>
                      Try a different search term or category
                    </div>
                    {sourceHandleId && (
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '12px', fontStyle: 'italic', textAlign: 'center' }}>
                        Available node types: {allowedCategories.join(', ')}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              filteredNodes.map((nodeType, index) => {
                const isHovered = hoveredNodeCode === nodeType.code;
                const combinedStyle: React.CSSProperties = {
                  ...nodeCardStyle,
                  ...(isHovered && {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }),
                };

                return (
                  <div
                    key={`${nodeType.code}-${index}`}
                    style={combinedStyle}
                    onClick={() => handleSelectNode(nodeType)}
                    onMouseEnter={() => handleNodeMouseEnter(nodeType.code)}
                    onMouseLeave={handleNodeMouseLeave}
                  >
                    <div style={nodeHeaderStyle}>
                      {renderNodeIcon(nodeType)}
                      <span style={nodeNameStyle}>{nodeType.displayName}</span>
                    </div>
                    <div style={nodeDescStyle}>{nodeType.description}</div>
                    <div style={nodeCategoryStyle}>{nodeType.category}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};
